import React from 'react';
import { FormattedMessage } from 'react-intl';

import { useIdelinkTheme } from '../../../configs/IdeLinkTheme';
import { ItemState } from '../../../services/commons/ItemState';
import { AdminPermission } from '../../../services/commons/Me';
import { AdminPermissions, AdminPermissionsOnModule, } from '../../../services/commons/Permissions/AdminPermissions';
import {
    ContentPermission,
    UserPermissions,
    UserPermissionsOnContent,
} from '../../../services/commons/Permissions/ContentPermission';
import { PermissionsAbilityHelper } from '../../../services/commons/Permissions/PermissionsAbilityHelper';
import { PermissionType } from '../../../services/commons/Permissions/PermissionType';
import { TabPermissionItem } from '../../BackUsers/Pages/components/PermissionsTreeContainer/PermissionsTreeContainer';

export interface UserPermissionsOnCollection extends UserPermissionsOnContent {
    //* PRODUCTS
    canAddProduct?: boolean;
    addProductEnabled?: boolean;
    saveProductEnabled?: boolean;
    canImportProducts?: boolean;
    importProductsEnabled?: boolean;
    removeProductEnabled?: boolean;

    productTitleEnabled?: boolean;
    productExternalReferenceEnabled?: boolean;
    productDescriptionEnabled?: boolean;
    productImage?: {
        canAdd?: boolean;
        canReplace?: boolean;
        canRemove?: boolean;
    };
    productPdfEnabled?: boolean;
}

export class CollectionsPermissions {
    public static COLLECTIONS_TAB_HASH = '#rights-collections';
    public static COLLECTIONS_CODE = 'Collections';

    public static Collections: AdminPermissionsOnModule = {};

    public static getCollectionsTab = (): TabPermissionItem => ({
        hash: CollectionsPermissions.COLLECTIONS_TAB_HASH,
        label: <FormattedMessage id="collections.title" />,
        getIcon: () => useIdelinkTheme().collections.permissionIcon,
        getIconSelected: () => useIdelinkTheme().collections.permissionSelectedIcon,
    });

    public static initializeCollectionsAdminPermissions = (perms: Array<AdminPermission> = []) => {
        const permsOnCollections = AdminPermissions.filter(
            perms,
            CollectionsPermissions.COLLECTIONS_CODE
        );
        CollectionsPermissions.Collections = AdminPermissions.toPermissions(permsOnCollections);
        PermissionsAbilityHelper.logPermissions('Collections', CollectionsPermissions.Collections);
    };

    public static toDefaultCollectionPermissions = (): UserPermissionsOnCollection => {
        const rawPermissions: UserPermissionsOnCollection =
            CollectionsPermissions.toUserPermissionsOnCollection(ItemState.Draft);
        const permissions: { [field: string]: boolean } = {};
        Object.keys(rawPermissions).forEach((key) => (permissions[key] = true));
        return permissions as UserPermissionsOnCollection;
    };

    public static toUserPermissionsOnCollection = (
        contentState: ItemState,
        contentPermission?: ContentPermission
    ): UserPermissionsOnCollection => {
        const canEdit = UserPermissions.canEdit(contentState, contentPermission);
        const isDraft =
            contentState === ItemState.Draft &&
            UserPermissions.has(PermissionType.ContentEditDraft, contentPermission);

        const isFinished =
            contentState === ItemState.Finished &&
            UserPermissions.has(PermissionType.ContentEditPublished, contentPermission);

        const isPublishedOrUnpublished =
            (contentState === ItemState.Published &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission)) ||
            (contentState === ItemState.Unpublished &&
                UserPermissions.has(PermissionType.ContentEditDraft, contentPermission));

        return {
            ...UserPermissions.toUserPermissionsOnContent(contentState, contentPermission),
            canEditPdf: canEdit,
            canSave: canEdit,
            canSendManualNotification: isFinished || isPublishedOrUnpublished,

            //* PRODUCTS
            canAddProduct: isDraft,
            addProductEnabled: !isPublishedOrUnpublished,
            canImportProducts: isDraft,
            importProductsEnabled: canEdit,
            removeProductEnabled: isDraft,
            saveProductEnabled: canEdit,

            productTitleEnabled: canEdit,
            productDescriptionEnabled: canEdit,
            productExternalReferenceEnabled: isDraft,
            productPdfEnabled: canEdit,
            productImage: {
                canAdd: canEdit,
                canRemove: canEdit,
                canReplace: canEdit,
            },
        };
    };
}
